<template>
  <tx-dialog
    v-model="isVisible" :title="title" :show-ok-cancel="!showOkCancelDiscard" :show-ok-cancel-discard="showOkCancelDiscard"
    :loading="loading" width="30%" :confirm-text="confirmTxt" :cancel-text="cancelTxt"
    @cancel="onCancel" @ok="onConfirm" @discard="onDiscard"
  >
    <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
    <div class="text-xl">
      {{ message }}
    </div>
    <div v-if="confirmContent.length" class="mt-4">
      <div
        v-for="(content, index) in confirmContent"
        :key="index"
        v-sanitize-html="typeof content === 'object' && content.header ? content.header : content"
        class="mb-2 text-l"
      />
    </div>
  </tx-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useErrorMessage from '../composables/errorMessage'
import TxDialog from './TxDialog.vue'
import TxAlert from './TxAlert.vue'

const { t } = useI18n()
const { errorMessage, hasError } = useErrorMessage()

const isVisible = ref(false)
const title = ref('')
const message = ref('')
const confirmContent = ref<(string | { header: string })[]>([])
const confirmTxt = ref<string>()
const cancelTxt = ref<string>()
const loading = ref(false)
const showOkCancelDiscard = ref(false)
let resolvePromise: (value: 'confirm' | 'cancel' | 'discard') => void
let action: (() => Promise<void>) | undefined
let discardAction: (() => Promise<void>) | undefined

function show(confirmTitle: string, confirmMessage: string, confirmContentArray: any[], asyncAction?: () => Promise<void>, asyncDiscardAction?: () => Promise<void>, confirmText?: string, cancelText?: string, displayOkCancelDiscard: boolean = false) {
  title.value = confirmTitle
  message.value = confirmMessage
  confirmContent.value = confirmContentArray || []
  isVisible.value = true
  loading.value = false
  errorMessage.value = ''
  action = asyncAction
  discardAction = asyncDiscardAction
  confirmTxt.value = confirmText
  cancelTxt.value = cancelText
  showOkCancelDiscard.value = displayOkCancelDiscard
  return new Promise<'confirm' | 'cancel' | 'discard'>((resolve) => {
    resolvePromise = resolve
  })
}

async function onConfirm() {
  loading.value = true
  try {
    if (action) {
      await action()
    }
    resolvePromise('confirm')
    isVisible.value = false
  }
  catch (error) {
    // Handle error if needed
    console.error('Action failed:', error)
    errorMessage.value = t('general.unexpectedError')
    resolvePromise('cancel')
  }
  finally {
    loading.value = false
  }
}

function onCancel() {
  isVisible.value = false
  resolvePromise('cancel')
}

async function onDiscard() {
  loading.value = true
  try {
    if (discardAction) {
      await discardAction()
    }
    resolvePromise('discard')
    isVisible.value = false
  }
  catch (error) {
    // Handle error if needed
    console.error('Action failed:', error)
    errorMessage.value = t('general.unexpectedError')
    resolvePromise('cancel')
  }
  finally {
    loading.value = false
  }
}

defineExpose({
  show,
})
</script>
